import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useApiClient from '../api/useApiClient';
import { PAYMENT_RETURN_SETTLED_EVENT } from '../../views/restaurant-onboarding/phases/payoutsPhaseHelpers';

/**
 * The restaurant's online-payment status, loaded once for the wizard shell.
 *
 * Like Business details, the Online payment step has no onboarding step behind it:
 * the backend derives the status from the link, the details marker and the provider
 * webhooks, so the shell needs this record to draw the step bar, and the phase body
 * reads and refreshes the same copy.
 *
 * Several callers race here — the shell's read, the phase's read on arrival, the
 * return poll, and the answers the phase writes back — so every read and every
 * write takes a ticket, and only the newest ticket may land. Without that, a read
 * already in flight when the partner links a provider would put the old state back.
 */
export default function usePaymentSetup(companyId) {
  const { CompanyPaymentSetupManager } = useApiClient();

  const [setup, setSetupState] = useState(null);
  const [loading, setLoading] = useState(Boolean(companyId));
  const [failed, setFailed] = useState(false);
  const latestTicket = useRef(0);

  // Another restaurant's status must never stand in for this one's — it would
  // mark Online payment complete for the wrong restaurant.
  useEffect(() => {
    latestTicket.current += 1;
    setSetupState(null);
    setFailed(false);
  }, [companyId]);

  const load = useCallback(async () => {
    if (!companyId) {
      setLoading(false);

      return null;
    }

    latestTicket.current += 1;
    const ticket = latestTicket.current;
    setLoading(true);

    try {
      const response = await CompanyPaymentSetupManager.show();
      const loaded = response?.success ? (response.data?.data ?? null) : null;

      if (ticket === latestTicket.current) {
        setFailed(!loaded);

        if (loaded) {
          setSetupState(loaded);
        }
      }

      return loaded;
    } catch {
      if (ticket === latestTicket.current) {
        setFailed(true);
      }

      return null;
    } finally {
      if (ticket === latestTicket.current) {
        setLoading(false);
      }
    }
  }, [CompanyPaymentSetupManager, companyId]);

  /** A write's answer is the newest state there is; any read still in flight is stale. */
  const setSetup = useCallback((next) => {
    latestTicket.current += 1;
    setFailed(false);
    setLoading(false);
    setSetupState(next);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Back from the provider, one reader polls; every other copy of the status on
  // screen (the banner, the settings action, the step bar) catches up once it is done.
  useEffect(() => {
    const reloadAfterReturn = () => {
      load();
    };

    window.addEventListener(PAYMENT_RETURN_SETTLED_EVENT, reloadAfterReturn);

    return () => window.removeEventListener(PAYMENT_RETURN_SETTLED_EVENT, reloadAfterReturn);
  }, [load]);

  return useMemo(() => ({ setup, loading, failed, reload: load, setSetup }), [setup, loading, failed, load, setSetup]);
}
